import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportCashFlow } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

export const CashFlowReport: React.FC = () => {
  const [singleDate, setSingleDate] = useState<dayjs.Dayjs>(dayjs());

  const singleDateStr = singleDate.format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["cash-flow", singleDateStr],
    queryFn: () =>
      GetSalesReportCashFlow({
        start_date: singleDateStr,
        end_date: singleDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const cashIn = reportData.reduce(
      (sum: number, item: any) => sum + (item.inflow || 0),
      0,
    );
    const cashOut = reportData.reduce(
      (sum: number, item: any) => sum + (item.outflow || 0),
      0,
    );
    const netCash = cashIn - cashOut;
    return [
      {
        title: "Total Cash Inflow (All Sources)",
        value: formatLKR(cashIn),
        colorClass: "text-emerald-600",
      },
      {
        title: "Total Cash Outflow (All Expenses)",
        value: formatLKR(cashOut),
        colorClass: "text-rose-600",
      },
      {
        title: "Net Cash Flow Balance",
        value: formatLKR(netCash),
        colorClass: netCash >= 0 ? "text-amber-700" : "text-rose-700",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Transaction Time",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Transaction Type",
      dataIndex: "type",
      key: "type",
      render: (v: string, record: any) => {
        const isInflow = (record.inflow || 0) > 0;
        const color = isInflow ? "success" : "error";
        return (
          <Tag
            color={color}
            className="rounded-full font-bold text-[10px] uppercase"
          >
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Amount Transacted",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number, record: any) => {
        const isInflow = (record.inflow || 0) > 0;
        const color = isInflow ? "text-emerald-600" : "text-rose-600";
        const prefix = isInflow ? "+" : "-";
        const amt = isInflow ? record.inflow || 0 : record.outflow || 0;
        return (
          <span className={`text-xs font-bold ${color}`}>
            {prefix}LKR{" "}
            {amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      title: "Running Balance",
      dataIndex: "running_balance",
      key: "running_balance",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-905">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Note / Description",
      dataIndex: "note",
      key: "note",
      render: (v: string) => (
        <span className="text-xs text-gray-600 font-medium">{v || "—"}</span>
      ),
    },
    {
      title: "Logged By Employee",
      dataIndex: "creator_name",
      key: "creator_name",
      render: (v: string) => (
        <span className="text-xs font-semibold text-gray-800">
          {v || "System"}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date
        </span>
        <DatePicker
          value={singleDate}
          onChange={(date) => date && setSingleDate(date)}
          allowClear={false}
          className="h-9 w-48 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Cash Book (Cash In / Out)"
      description="Review cash drawer openings, direct drawer deposits, and cash withdrawals."
      icon={<CreditCard className="w-6 h-6 text-amber-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.id || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
